import { arredondarBrl, calcularRemessaExteriorEstimativa, linhaElegivelRemessaExterior, ETAPAS_REMESSA, ajusteNfEtapa, calcularImpostoNfAccess, camposAjusteNf } from './remessaExteriorEstimativaPolicy';
import { normalizarPagamentoExterior } from './pagamentoExteriorPolicy';
import { FeeIntelligence } from './feeIntelligence';
import { ChiesiAgenciaImpostoPolicy } from './chiesiAgenciaImpostoPolicy';
import { AgenciaOrcamentoPolicy } from './agenciaOrcamentoPolicy';
import { anotarNfAccessNaBaseFee, baseFeeFechamentoInternacionalBrl, clienteBaseFeeComImpostoItem, clienteBaseFeeComNfAccess, clienteBaseFeeComServico } from './feeBaseInternacionalPolicy';
import { cambioBrlLinha, moedaPropriaEtapa, normalizarMoeda, temCambioProprio } from './moedaFornecedorPolicy';
import { ImpostoNfAccessPorFornecedorPolicy } from './impostoNfAccessPorFornecedorPolicy';
import { etapaEstimativaLinha, resolverColunaVigentePorFornecedor } from './colunaVigentePolicy';
import { identificarLinhaOrcamento } from './linhaOrcamentoIdentificacaoPolicy';
import { convidadosEsperadosDoEvento, paxConvidadosEsperados } from './convidadosEsperadosPolicy';
import { ehSomenteVisualizacao } from './somenteVisualizacaoPolicy';
import { ehImpostoNfContaDireta, fatorBaseNf } from './impostoNfContaDiretaPolicy';

export const MOEDAS_EXTERIOR = { real: 'BRL', dolar: 'USD', euro: 'EUR', iene: 'JPY', libra: 'GBP', dolarcad: 'CAD' };
const LIMITE_NUMERICO_EXTERIOR = 999999999999.99;
export const numeroExterior = (valor) => {
    if (valor === null || valor === undefined || String(valor).trim() === '') return null;
    const texto = String(valor).trim();
    const numero = Number(texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto);
    return Number.isFinite(numero) ? numero : null;
};
const centavos = (valor) => Math.round(arredondarBrl(valor) * 100);
const dataValida = (valor) => /^\d{4}-\d{2}-\d{2}$/.test(valor || '') && !Number.isNaN(Date.parse(valor)) && new Date(valor).toISOString().slice(0, 10) === valor;
const CAMPOS_PERCENTUAIS = ['iof_percentual', 'ir_percentual', 'iof_brl_override', 'ir_brl_override'];
const temCampo = (objeto, campo) => Object.prototype.hasOwnProperty.call(objeto || {}, campo);
const temContratoPercentual = (transacao = {}) => CAMPOS_PERCENTUAIS.some((campo) => temCampo(transacao, campo));
const overrideOuNull = (valor) => valor === null || valor === undefined || String(valor).trim() === '' ? null : numeroExterior(valor);
const excedeLimiteNumerico = (...valores) => valores.some((valor) => valor !== null && valor > LIMITE_NUMERICO_EXTERIOR);

export function resolverCustosPercentuaisTransacao(transacao = {}) {
    const legado = !temContratoPercentual(transacao);
    if (legado) return {
        legado: true,
        iof_percentual: null,
        ir_percentual: null,
        iof_brl_override: null,
        ir_brl_override: null,
        iof_brl: numeroExterior(transacao.iof_brl),
        ir_brl: numeroExterior(transacao.ir_brl),
    };
    const original = numeroExterior(transacao.valor_original);
    const cambio = transacao.moeda === 'real' ? 1 : numeroExterior(transacao.cambio);
    const principal = original !== null && cambio !== null ? arredondarBrl(arredondarBrl(original) * cambio) : null;
    const iofPercentual = numeroExterior(temCampo(transacao, 'iof_percentual') ? transacao.iof_percentual : 3.5);
    const irPercentual = numeroExterior(temCampo(transacao, 'ir_percentual') ? transacao.ir_percentual : 0);
    const iofOverride = overrideOuNull(transacao.iof_brl_override);
    const irOverride = overrideOuNull(transacao.ir_brl_override);
    const calcular = (percentual, override) => override !== null ? arredondarBrl(override)
        : principal !== null && percentual !== null ? arredondarBrl(principal * percentual / 100) : null;
    return {
        legado: false,
        iof_percentual: iofPercentual,
        ir_percentual: irPercentual,
        iof_brl_override: iofOverride,
        ir_brl_override: irOverride,
        iof_brl: calcular(iofPercentual, iofOverride),
        ir_brl: calcular(irPercentual, irOverride),
    };
}

// Ledger declarativo: não cria título, não registra baixa e não presume imposto pago.
export function validarConfiguracaoFechamentoExterior(config) {
    if (config === null || config === undefined) return null;
    if (!config || typeof config !== 'object' || Array.isArray(config)) return 'Fechamento exterior inválido.';
    const moeda = config.invoice?.moeda;
    const invoice = numeroExterior(config.invoice?.valor_original);
    if (!MOEDAS_EXTERIOR[moeda]) return 'Selecione a moeda da invoice.';
    if (invoice === null || invoice <= 0 || centavos(invoice) <= 0) return 'Informe o valor original positivo da invoice.';
    if (excedeLimiteNumerico(invoice)) return 'Valor da invoice excede o limite numérico permitido.';
    const concluido = config.concluido ?? false;
    const transacoes = config.transacoes ?? [];
    if (typeof concluido !== 'boolean') return 'Informe se o fechamento está concluído.';
    if (!Array.isArray(transacoes) || transacoes.length > 100) return 'Informe no máximo 100 transações.';
    const ids = new Set();
    let pago = 0;
    for (const [index, transacao] of transacoes.entries()) {
        const prefixo = `Transação ${index + 1}: `;
        const id = String(transacao?.id ?? '').trim();
        if (!id || id.length > 100 || ids.has(id)) return prefixo + 'identificador ausente ou repetido.';
        ids.add(id);
        if (!dataValida(transacao.data)) return prefixo + 'informe uma data válida.';
        if (!['cartao_credito', 'remessa'].includes(transacao.modalidade)) return prefixo + 'selecione cartão ou remessa.';
        if (transacao.moeda !== moeda) return prefixo + 'a moeda deve ser a mesma da invoice.';
        const original = numeroExterior(transacao.valor_original);
        const cambio = numeroExterior(transacao.cambio);
        if (original === null || original <= 0 || centavos(original) <= 0) return prefixo + 'informe o principal original positivo.';
        if (cambio === null || cambio <= 0 || (moeda === 'real' && cambio !== 1)) return prefixo + 'informe câmbio positivo (BRL = 1).';
        if (excedeLimiteNumerico(original, cambio)) return prefixo + 'principal original ou câmbio excede o limite numérico permitido.';
        const custos = resolverCustosPercentuaisTransacao(transacao);
        const envio = numeroExterior(transacao.taxa_envio_brl);
        if (envio === null || envio < 0) return prefixo + 'informe a taxa de envio real em BRL, inclusive 0.';
        if (excedeLimiteNumerico(envio)) return prefixo + 'taxa de envio excede o limite numérico permitido.';
        if (custos.legado) {
            if (custos.iof_brl === null || custos.iof_brl < 0 || custos.ir_brl === null || custos.ir_brl < 0) return prefixo + 'informe IOF e IR reais em BRL, inclusive 0.';
            if (excedeLimiteNumerico(custos.iof_brl, custos.ir_brl)) return prefixo + 'IOF ou IR excede o limite numérico permitido.';
        } else {
            for (const percentual of [custos.iof_percentual, custos.ir_percentual]) {
                if (percentual === null || percentual < 0 || percentual >= 100) return prefixo + 'informe percentuais de IOF e IR entre 0 e 100.';
            }
            for (const override of [custos.iof_brl_override, custos.ir_brl_override]) {
                if (override !== null && override < 0) return prefixo + 'override de IOF/IR deve ser um valor BRL não negativo.';
            }
            if (excedeLimiteNumerico(custos.iof_brl_override, custos.ir_brl_override, custos.iof_brl, custos.ir_brl)) return prefixo + 'IOF ou IR excede o limite numérico permitido.';
            for (const campo of ['iof_brl', 'ir_brl']) {
                if (!temCampo(transacao, campo) || transacao[campo] === undefined) continue;
                const informado = numeroExterior(transacao[campo]);
                if (informado === null || centavos(informado) !== centavos(custos[campo])) return prefixo + `${campo === 'iof_brl' ? 'IOF' : 'IR'} final inconsistente com percentual/override.`;
            }
        }
        pago += centavos(original);
    }
    if (pago > centavos(invoice)) return 'O principal pago supera o valor original da invoice.';
    if (concluido && pago !== centavos(invoice)) return 'Para concluir, a soma original das transações deve conciliar exatamente com a invoice.';
    return null;
}

// ISAD #801 (21/10): transações válidas e principal pago = invoice → o registro já pode ser concluído
// (o modal marca "Concluir fechamento" sozinho; sem isso IOF/IR/envio/NF não entram no Fechamento).
export function fechamentoExteriorConciliado(config) {
    if (!config || !Array.isArray(config.transacoes) || config.transacoes.length === 0) return false;
    return validarConfiguracaoFechamentoExterior({ ...config, concluido: true }) === null;
}

export function prepararFechamentoExteriorPayload(config) {
    if (config === null || config === undefined) return config;
    const erro = validarConfiguracaoFechamentoExterior(config);
    if (erro) throw new Error(erro);
    return { invoice: { moeda: config.invoice.moeda, valor_original: arredondarBrl(numeroExterior(config.invoice.valor_original)) }, concluido: config.concluido ?? false,
        transacoes: (config.transacoes ?? []).map((transacao) => {
            const custos = resolverCustosPercentuaisTransacao(transacao);
            return { id: String(transacao.id).trim(), data: transacao.data, modalidade: transacao.modalidade, moeda: transacao.moeda,
                valor_original: arredondarBrl(numeroExterior(transacao.valor_original)), cambio: transacao.moeda === 'real' ? 1 : numeroExterior(transacao.cambio),
                iof_brl: arredondarBrl(custos.iof_brl), ir_brl: arredondarBrl(custos.ir_brl), taxa_envio_brl: arredondarBrl(numeroExterior(transacao.taxa_envio_brl)),
                ...(custos.legado ? {} : { iof_percentual: custos.iof_percentual, ir_percentual: custos.ir_percentual,
                    iof_brl_override: custos.iof_brl_override, ir_brl_override: custos.ir_brl_override }),
            };
        }),
    };
}

export function calcularFechamentoExterior(linha = {}, fornecedorAccess = null) {
    if (!linhaElegivelRemessaExterior(linha, fornecedorAccess)) return { elegivel: false, ativo: false };
    const config = linha.fechamento_exterior;
    if (config === null || config === undefined) return { elegivel: true, ativo: false };
    const erro = validarConfiguracaoFechamentoExterior(config);
    const resultado = {
        elegivel: true, ativo: true, completo: !erro && config.concluido === true,
        concluido: config.concluido === true, erro,
        moeda_invoice: config.invoice?.moeda, invoice_original: arredondarBrl(numeroExterior(config.invoice?.valor_original) || 0),
        pago_original: 0, saldo_original: 0, principal_brl: 0, iof_brl: 0, ir_brl: 0, taxa_envio_brl: 0, transacoes: [],
    };
    (Array.isArray(config.transacoes) ? config.transacoes : []).forEach((transacao) => {
        const original = arredondarBrl(numeroExterior(transacao.valor_original) || 0);
        const cambio = transacao.moeda === 'real' ? 1 : numeroExterior(transacao.cambio) || 0;
        const principal = arredondarBrl(original * cambio);
        // Cartão: IOF 3,5% / IR 0 / envio 0 são só padrão sugerido; vale o que o operador digitar (Rafael 02/10).
        const percentuais = resolverCustosPercentuaisTransacao(transacao);
        const custos = { iof_brl: arredondarBrl(percentuais.iof_brl || 0), ir_brl: arredondarBrl(percentuais.ir_brl || 0), taxa_envio_brl: arredondarBrl(numeroExterior(transacao.taxa_envio_brl) || 0) };
        resultado.pago_original = arredondarBrl(resultado.pago_original + original);
        resultado.principal_brl = arredondarBrl(resultado.principal_brl + principal);
        Object.keys(custos).forEach((campo) => { resultado[campo] = arredondarBrl(resultado[campo] + custos[campo]); });
        resultado.transacoes.push({ ...transacao, valor_original: original, cambio, principal_brl: principal, ...custos,
            ...(percentuais.legado ? {} : { iof_percentual: percentuais.iof_percentual, ir_percentual: percentuais.ir_percentual,
                iof_brl_override: percentuais.iof_brl_override, ir_brl_override: percentuais.ir_brl_override }) });
    });
    resultado.saldo_original = arredondarBrl(resultado.invoice_original - resultado.pago_original);
    const custos = arredondarBrl(resultado.iof_brl + resultado.ir_brl + resultado.taxa_envio_brl);
    // Um único gross-up por linha, sobre custos BRL somados; nunca sobre invoice/principal.
    // Ajuste manual do Imposto NF Access no Fechamento (Inter 19/10): alíquota/modo da etapa 'fech'.
    const ajusteNf = ajusteNfEtapa(linha, 'fech');
    resultado.imposto_nf_brl = arredondarBrl(calcularImpostoNfAccess(custos, ajusteNf));
    Object.assign(resultado, camposAjusteNf(ajusteNf));
    resultado.adicional_brl = arredondarBrl(custos + resultado.imposto_nf_brl);
    resultado.total_brl = arredondarBrl(resultado.principal_brl + resultado.adicional_brl);
    return resultado;
}

// Item 13: pendências localizadas de TODAS as linhas. Ledger inválido leva ao botão "Registrar
// fechamento exterior" (coluna Fechamento); ledger em linha que não aceita exterior leva ao
// "Local do pagamento". Regra inalterada.
export function listarPendenciasFechamentoExterior(orcamento = {}, fornecedorAccess = null) {
    const pendencias = [];
    (orcamento.orcamentoitem || []).forEach((grupo, grupoIndex) => (grupo?.subitem || []).forEach((linha, linhaIndex) => {
        const local = `${identificarLinhaOrcamento(grupo, linhaIndex, linha, 'Serviço exterior')} (Fechamento)`;
        if (!linhaElegivelRemessaExterior(linha, fornecedorAccess)) {
            let exterior = false;
            try { exterior = normalizarPagamentoExterior(linha.pagamento_exterior) === true; } catch (error) { /* pagamento policy exibe o erro */ }
            if (exterior && linha.fechamento_exterior !== null && linha.fechamento_exterior !== undefined) {
                pendencias.push({ mensagem: `${local}: remova o fechamento exterior de uma linha que não aceita pagamento internacional.`,
                    grupoIndex, linhaIndex, etapa: null, campo: 'local_pagamento' });
            }
            return;
        }
        const erro = validarConfiguracaoFechamentoExterior(linha.fechamento_exterior);
        if (erro) pendencias.push({ mensagem: `${local}: ${erro}`, grupoIndex, linhaIndex, etapa: 'fechamento', campo: 'fechamento_exterior' });
    }));
    return pendencias;
}

export function validarFechamentoExterior(orcamento = {}, fornecedorAccess = null) {
    return listarPendenciasFechamentoExterior(orcamento, fornecedorAccess)[0]?.mensagem ?? null;
}

// Câmbio do Fechamento = câmbio da transação (Rafael 19/10): linha com moeda própria cujo Fechamento está na
// moeda da invoice (estrangeira). Registro antigo com mais de uma transação: média ponderada (R$ pago ÷ valor
// pago), a mesma conta do valor em R$. Null = não se aplica. Espelho: OrcamentoFechamentoExterior::cambioDaTransacao.
export function cambioDaTransacao(linha = {}) {
    const config = linha?.fechamento_exterior;
    const transacoes = config && Array.isArray(config.transacoes) ? config.transacoes : [];
    if (!transacoes.length || !temCambioProprio(linha)) return null;
    const moeda = normalizarMoeda(config?.invoice?.moeda || 'real');
    if (moeda === 'real' || moedaPropriaEtapa(linha, 'Fechamento') !== moeda) return null;
    let original = 0;
    let brl = 0;
    transacoes.forEach((transacao) => {
        const valor = numeroOuZero(transacao?.valor_original);
        original += valor;
        brl += Math.round(valor * numeroOuZero(transacao?.cambio) * 100) / 100;
    });
    if (!(original > 0) || !(brl > 0)) return null;
    return transacoes.length === 1 ? numeroOuZero(transacoes[0].cambio) : Math.round((brl / original) * 1e6) / 1e6;
}

// A cada recálculo da tela: linha com transação registrada fica com o câmbio da transação no Fechamento
// (a API grava a mesma regra). Devolve o mesmo array quando nada muda.
export function sincronizarCambioFechamentoTransacao(orcamentoitem = []) {
    let mudou = false;
    const grupos = (orcamentoitem || []).map((grupo) => {
        if (!grupo || !Array.isArray(grupo.subitem)) return grupo;
        let mudouGrupo = false;
        const subitem = grupo.subitem.map((linha) => {
            const cambio = cambioDaTransacao(linha);
            if (cambio === null || Math.abs(numeroOuZero(linha.cambioFechamento) - cambio) < 1e-7) return linha;
            mudouGrupo = true;
            return { ...linha, cambioFechamento: cambio };
        });
        if (!mudouGrupo) return grupo;
        mudou = true;
        return { ...grupo, subitem };
    });
    return mudou ? grupos : orcamentoitem;
}

// Rafael 15/10 ("o que preencher no modal tem que substituir a coluna Fechamento — é o valor final"):
// "Aplicar à linha" grava na coluna Fechamento o PRINCIPAL pago (Σ original × câmbio de cada
// transação), também em parcial (decisão 15/10; antes parcial não mexia na coluna). IOF/IR/envio/NF
// continuam nas linhas "↳" da remessa — nunca dentro da coluna (contaria em dobro). Coluna na moeda
// da linha: principal ÷ câmbio da linha no Fechamento; qtd/diária 1 e serviço 0. % Impostos e
// % Imposto NF valem para as 3 colunas, então o unitário compensa para o total bater no principal.
// Sem transação válida (ou "Limpar registro") a coluna fica como está.
export function colunaFechamentoDoLedger(orcamento = {}, linha = {}, config) {
    if (config === null || config === undefined) return null;
    const calculado = calcularFechamentoExterior({ ...linha, fechamento_exterior: config });
    if (!calculado.ativo || calculado.erro || !(calculado.principal_brl > 0)) return null;
    const cambioGlobal = orcamento.moeda === 'real' || !orcamento.moeda ? 1 : (numeroOuZero(orcamento.cambio) || 1);
    // Rafael 15/10: invoice na MESMA moeda da coluna Fechamento → a coluna mostra o original pago (ex.: US$ 100).
    // Rafael 19/10: o câmbio do Fechamento passa a ser o da transação também na moeda do orçamento (não há mais
    // câmbio do topo), então o R$ da coluna bate com o R$ pago. Moedas diferentes: converte.
    const moedaFech = temCambioProprio(linha) ? moedaPropriaEtapa(linha, 'Fechamento') : normalizarMoeda(orcamento.moeda);
    const naMoedaDaInvoice = moedaFech === calculado.moeda_invoice && calculado.pago_original > 0;
    const cambioTransacao = naMoedaDaInvoice ? cambioDaTransacao({ ...linha, fechamento_exterior: config }) : null;
    const gravaCambio = cambioTransacao !== null;
    const cambioLinha = gravaCambio ? cambioTransacao
        : (numeroOuZero(cambioBrlLinha(linha, 'Fechamento', cambioGlobal)) || 1);
    const alvo = naMoedaDaInvoice ? calculado.pago_original : calculado.principal_brl / cambioLinha;
    const impostos = numeroOuZero(linha.impostoPorcentagemItem);
    const impostoNf = numeroOuZero(linha.imposto);
    let unitario = alvo / (1 + impostos / 100);
    // D-0112: desfaz o % Imposto NF na conta da linha (direta = ÷ (1 + a); padrão, × (1 − a)).
    if (impostoNf > 0 && impostoNf < 100) unitario = ehImpostoNfContaDireta(linha) ? unitario / (1 + impostoNf / 100) : unitario * (1 - impostoNf / 100);
    return { valorUnitarioFechamento: Math.round(unitario * 1e6) / 1e6, quantidadeFechamento: 1, diariaFechamento: 1,
        servicoFechamento: 0, totalFechamento: Math.round(alvo * 100) / 100,
        ...(gravaCambio ? { cambioFechamento: Math.round(cambioLinha * 1e6) / 1e6 } : {}) };
}

export function alterarFechamentoExteriorLinha(orcamento, grupoIndex, linhaIndex, config) {
    return { ...orcamento, orcamentoitem: orcamento.orcamentoitem.map((grupo, g) => g !== grupoIndex ? grupo : {
        ...grupo, subitem: grupo.subitem.map((linha, l) => l !== linhaIndex ? linha
            : { ...linha, ...(colunaFechamentoDoLedger(orcamento, linha, config) || {}), fechamento_exterior: config }),
    }) };
}

export function limparFechamentoExteriorAoCopiar(orcamento) {
    const copia = { ...orcamento, orcamentoitem: (orcamento.orcamentoitem || []).map((grupo) => ({ ...grupo, subitem: (grupo.subitem || []).map((linha) => {
        const clone = { ...linha, fechamento_exterior: null };
        delete clone.fechamento_exterior_calculado;
        delete clone.fee_fechamento_real_calculado;
        delete clone.fechamento_exibicao;
        return clone;
    }) })) };
    copia.orcamentoitem.forEach((grupo) => { delete grupo.fechamento_exibicao; });
    delete copia.fechamento_exibicao;
    Object.keys(copia).filter((chave) => chave.startsWith('fechamento_real_') || ['totalRealFechamentoBRL', 'totalRealAtualBRL'].includes(chave)).forEach((chave) => { delete copia[chave]; });
    return copia;
}

// Valor de exibição do Fechamento é derivado do ledger: nunca vai para o payload de gravação.
export function removerExibicaoFechamentoExterior(orcamento = {}) {
    const limpo = { ...orcamento, orcamentoitem: (orcamento.orcamentoitem || []).map((grupo) => {
        const { fechamento_exibicao: grupoExibicao, ...restoGrupo } = grupo;
        return { ...restoGrupo, subitem: (grupo.subitem || []).map((linha) => {
            const { fechamento_exibicao: linhaExibicao, ...restoLinha } = linha;
            return restoLinha;
        }) };
    }) };
    delete limpo.fechamento_exibicao;
    return limpo;
}

// Fechamento exibido na grade/totais: o pago de fato quando há ledger concluído, senão o gravado.
export const valorFechamentoExibicao = (linha = {}) => linha.fechamento_exibicao ? linha.fechamento_exibicao.totalFechamento : linha.totalFechamento;
export const linhaFechamentoExteriorConcluido = (linha = {}) => linha.fechamento_exibicao?.origem === 'ledger';

export function resumirFechamentoExterior(orcamento = {}, fornecedorAccess = null) {
    const linhas = (orcamento.orcamentoitem || []).flatMap((grupo) => grupo.subitem || []).map((linha) => ({ linha, calculado: calcularFechamentoExterior(linha, fornecedorAccess) })).filter(({ calculado }) => calculado.ativo);
    const resumo = { ativo: linhas.length > 0, completo: linhas.length > 0 && linhas.every(({ calculado }) => calculado.completo), quantidade_linhas: linhas.length, linhas };
    ['principal_brl', 'iof_brl', 'ir_brl', 'taxa_envio_brl', 'imposto_nf_brl', 'adicional_brl', 'total_brl'].forEach((campo) => { resumo[campo] = arredondarBrl(linhas.reduce((soma, { calculado }) => soma + calculado[campo], 0)); });
    return resumo;
}

export function calcularAuxilioIof(transacao) {
    const original = numeroExterior(transacao.valor_original);
    const cambio = transacao.moeda === 'real' ? 1 : numeroExterior(transacao.cambio);
    if (original === null || original <= 0 || cambio === null || cambio <= 0) return null;
    return arredondarBrl(arredondarBrl(arredondarBrl(original) * cambio) * 0.035);
}

const flag = (valor) => [true, 1, '1', 'sim', 'true'].includes(valor);
const nome = (valor) => String(valor || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
const numeroOuZero = (valor) => numeroExterior(valor) || 0;
// Regra única do Total Atual (tela = API): a coluna é a vigente no AGREGADO do fornecedor da linha
// (colunaVigentePolicy.resolverColunaVigentePorFornecedor — última coluna preenchida, 25/09; A01 28/09). Linha Brasil entra com o valor dessa
// coluna (0 se não preenchida); linha Exterior elegível ainda aberta entra com a etapa comercial
// da estimativa (principal e encargos da mesma etapa); null = sem custo no Atual.
const momentoAtualLinha = (linha, calculado, fornecedorAccess, etapaVigente) => {
    if (!calculado.completo && linhaElegivelRemessaExterior(linha, fornecedorAccess)) {
        const etapa = etapaEstimativaLinha(linha, etapaVigente);
        return etapa ? ETAPAS_REMESSA[etapa] : null;
    }
    return etapaVigente;
};
const imposto = (base, percentual = 19.05) => arredondarBrl(base * percentual / (100 - percentual));
// Linha 1945 do grupo SERVIÇOS AGÊNCIA (Chiesi) em orçamento em moeda estrangeira: a NF é gravada na
// moeda do orçamento (arredondada ali) e a API usa esse valor × câmbio — US$ 26,57 × 5 = R$ 132,85, não
// R$ 132,87 calculados direto em reais (item 12, opção A). Em BRL (câmbio 1) nada muda.
export const impostoNaMoedaDoOrcamento = (baseBrl, cambio = 1, percentual = 19.05) => {
    const fator = Number.isFinite(cambio) && cambio > 0 ? cambio : 1;
    return arredondarBrl(imposto(baseBrl / fator, percentual) * fator);
};
// Mesma base da API (OrcamentoImpostoNfAccess::calcularBasesGrupo): sem linhas de imposto, sem
// principal pago no exterior e sem linha que já cobra % Imposto NF próprio. Exceção, como na tela
// (linhaNaBaseImpostoNfAccessGrupo): coordenação Sanofi/Chiesi paga no exterior fica na base (#786, 21/10).
const impostoNfProprio = (linha, fornecedorAccess) => {
    const percentual = numeroOuZero(linha.imposto);
    return percentual > 0 && percentual < 100 && ImpostoNfAccessPorFornecedorPolicy.fornecedorEhAccess(linha, fornecedorAccess);
};
const compoeBaseNfProprio = (linha, fornecedorAccess) => !ehSomenteVisualizacao(linha) && !['1945', 'IMPOSTO_NF_ACCESS'].includes(String(linha.produto?.codigo ?? '').trim().toUpperCase())
    && flag(linha.inserirCalculoTotalPlanilha) && (!!linha.nfAccessPrincipalExteriorChiesi || !linhaElegivelRemessaExterior(linha, fornecedorAccess))
    && !impostoNfProprio(linha, fornecedorAccess);
const ROTULO_MOEDA = { real: 'BRL', dolar: 'USD', euro: 'EUR', iene: 'JPY', libra: 'GBP', dolarcad: 'CAD' };
const numeroBr = (valor, casas = 2) => {
    const [inteiro, decimal] = Math.abs(valor).toFixed(casas).split('.');
    return (valor < 0 ? '-' : '') + inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + (decimal ? `,${decimal}` : '');
};
const ENCARGOS_REAIS = ['imposto_remessa_brl', 'taxa_envio_brl', 'imposto_nf_brl'];

// Coluna Fechamento de EXIBIÇÃO (planilha do cliente: "Fechamento mostra o que foi pago"). As colunas
// gravadas não mudam. Ledger concluído = principal pago ÷ câmbio global do orçamento; os encargos reais
// ficam nas linhas "↳" da remessa, também ÷ câmbio. FEE/NF recalculados pelo shadow real entram para
// que Σ coluna Fechamento × câmbio = totalRealFechamentoBRL, sem contar o principal duas vezes.
function exibicaoLinhaFechamento(linha, calculado, sombra, cambio) {
    if (calculado.completo) {
        const encargos = { imposto_remessa_brl: arredondarBrl(calculado.iof_brl + calculado.ir_brl), taxa_envio_brl: calculado.taxa_envio_brl, imposto_nf_brl: calculado.imposto_nf_brl };
        const encargosMoeda = {};
        ENCARGOS_REAIS.forEach((campo) => { encargosMoeda[campo] = encargos[campo] / cambio; });
        // Linha com moeda própria exibe na moeda dela (÷ câmbio da linha); legado ÷ câmbio global.
        const total = calculado.principal_brl / cambioBrlLinha(linha, 'Fechamento', cambio);
        const cambioMedio = calculado.pago_original > 0 ? calculado.principal_brl / calculado.pago_original : null;
        return { origem: 'ledger', cambio_orcamento: cambio, totalFechamento: total, valorUnitarioFechamento: total,
            quantidadeFechamento: 1, diariaFechamento: 1, servicoFechamento: 0, impostoPorcentagemItem: 0,
            principal_brl: calculado.principal_brl, cambio_efetivo_medio: cambioMedio,
            moeda_invoice: calculado.moeda_invoice, invoice_original: calculado.invoice_original,
            encargos_brl: encargos, encargos_moeda_orcamento: encargosMoeda,
            encargos_total_moeda_orcamento: calculado.adicional_brl / cambio,
            observacao: `Fechamento exterior concluído: pago R$ ${numeroBr(calculado.principal_brl)}`
                + (cambioMedio !== null ? ` (câmbio efetivo médio ${numeroBr(cambioMedio, 4)})` : '')
                + ` · invoice ${ROTULO_MOEDA[calculado.moeda_invoice] || calculado.moeda_invoice} ${numeroBr(calculado.invoice_original)}` };
    }
    const totalSombra = numeroOuZero(sombra?.totalFechamento);
    // Só FEE/NF que o fechamento real recalculou; as demais linhas exibem o valor gravado.
    if (!sombra || Math.abs(totalSombra - numeroOuZero(linha.totalFechamento) * cambioBrlLinha(linha, 'Fechamento', cambio)) <= 0.005) return null;
    return { origem: 'recalculado', cambio_orcamento: cambio, totalFechamento: totalSombra / cambio, total_brl: arredondarBrl(totalSombra),
        observacao: `Recalculado pelo fechamento exterior concluído: R$ ${numeroBr(totalSombra)}` };
}

// Prévia metadata-only: os preços originais não são regravados em BRL.
// Fechamento e Atual têm shadows diferentes; o Atual segue a coluna vigente no AGREGADO.
export function decorarPreviewFechamentoExterior(orcamento, contexto = {}) {
    const fornecedorAccess = contexto.fornecedorAccess || null;
    const grupos = orcamento.orcamentoitem || [];
    const reais = grupos.flatMap((grupo) => grupo.subitem || []).map((linha) => calcularFechamentoExterior(linha, fornecedorAccess));
    // Opção (D-0113): não é paga, então não liga, não completa nem soma no fechamento real (espelho da API).
    const opcao = grupos.flatMap((grupo) => grupo.subitem || []).map((linha) => ehSomenteVisualizacao(linha));
    const ativo = reais.some((calculado, i) => !opcao[i] && calculado.ativo);
    const ativoAtual = reais.some((calculado, i) => !opcao[i] && calculado.completo);
    const elegiveis = grupos.flatMap((grupo) => grupo.subitem || []).filter((linha) => !ehSomenteVisualizacao(linha) && linhaElegivelRemessaExterior(linha, fornecedorAccess) &&(calcularFechamentoExterior(linha, fornecedorAccess).ativo || ['PreEvento', 'Negociado', 'Fechamento'].some((momento) => numeroOuZero(linha[`total${momento}`]) > 0)));
    const ledgerCompleto = ativo && elegiveis.every((linha) => calcularFechamentoExterior(linha, fornecedorAccess).completo);
    const cambio = orcamento.moeda === 'real' ? 1 : numeroOuZero(orcamento.cambio);
    const cliente = orcamento.evento?.cliente || orcamento.cliente || {};
    const chiesi = nome(cliente.nome).includes('CHIESI');
    const internacional = nome(orcamento.evento?.nacionalidade || contexto.eventoNacionalidade) === 'INTERNACIONAL';
    const aplicaNf = chiesi || internacional;
    // Regra B (28/09): nacional Sanofi-família/Chiesi — FEE real com o % Serviço da linha na base
    // (linha com ledger concluído entra com o principal pago, sem serviço). Espelho da API.
    const baseComServico = !internacional && clienteBaseFeeComServico(cliente);
    const baseComImposto = !internacional && !baseComServico && clienteBaseFeeComImpostoItem(cliente);
    // D-0153: Chiesi nacional — FEE real com o Imposto NF Access da linha na base (espelho da API).
    const baseComNfAccess = baseComServico && clienteBaseFeeComNfAccess(cliente);
    const regras = (contexto.feeRules || []).filter((regra) => !orcamento.fee_versao || String(regra.versao || '') === String(orcamento.fee_versao));
    const metadataFees = new Map();
    const linhasVigente = grupos.flatMap((grupo) => grupo.subitem || []);
    const etapasVigente = resolverColunaVigentePorFornecedor(linhasVigente, {
        cambio: cambio > 0 ? cambio : 1,
        estadoLinha: (linha) => {
            const calculado = calcularFechamentoExterior(linha, fornecedorAccess);
            if (calculado.completo) return { estado: 'ledger', principal_brl: calculado.principal_brl };
            return { estado: linhaElegivelRemessaExterior(linha, fornecedorAccess) ? 'aberta' : 'normal', principal_brl: 0 };
        },
    }).etapas;
    const etapaPorLinha = new Map(linhasVigente.map((linha, i) => [linha, etapasVigente[i]]));
    const shadow = (atual) => {
        let regrasCompletas = true;
        let adicionaisEstimadosAtual = 0;
        const copia = grupos.map((grupo) => ({ ...grupo, produto: { ...grupo.produto }, subitem: (grupo.subitem || []).map((linha) => {
            const calculado = calcularFechamentoExterior(linha, fornecedorAccess);
            const etapaVigente = etapaPorLinha.get(linha) || 'PreEvento';
            const momento = atual ? momentoAtualLinha(linha, calculado, fornecedorAccess, etapaVigente) : 'Fechamento';
            const campo = (prefixo) => (momento ? linha[`${prefixo}${momento}`] : 0);
            // Moeda do fornecedor (27/09): shadow em BRL pelo câmbio da linha na etapa (legado: global).
            const cambioLinha = cambioBrlLinha(linha, momento || 'Fechamento', cambio);
            let adicionalEstimadoLinha = 0;
            if (atual && !calculado.completo && !ehSomenteVisualizacao(linha) && linhaElegivelRemessaExterior(linha, fornecedorAccess)) {
                const etapaAtual = etapaEstimativaLinha(linha, etapaVigente);
                if (etapaAtual) adicionalEstimadoLinha = calcularRemessaExteriorEstimativa(linha, orcamento, fornecedorAccess)[etapaAtual].adicional_brl;
                adicionaisEstimadosAtual += adicionalEstimadoLinha;
            }
            const produto = { ...linha.produto };
            // Internacional: FEE real sobre o que foi pago (principal + encargos), como na planilha.
            const feeBaseInternacional = internacional && !FeeIntelligence.isFeeCalculatorItem(produto)
                ? { fechamento: baseFeeFechamentoInternacionalBrl({ calculado, totalMomentoBrl: numeroOuZero(campo('total')) * cambioLinha, adicionalEstimadoBrl: adicionalEstimadoLinha, linha }) }
                : undefined;
            // O shadow já está em BRL: sem o fator de moeda do fornecedor nem a base R$ exata (feeIntelligence.rawBaseByMoment).
            const feeBaseComServico = baseComServico && !FeeIntelligence.isFeeCalculatorItem(produto) && !AgenciaOrcamentoPolicy.isFeeAgenciaGlobal(produto) ? true : undefined;
            const feeBaseComImpostoItem = baseComImposto && !FeeIntelligence.isFeeCalculatorItem(produto) && !AgenciaOrcamentoPolicy.isFeeAgenciaGlobal(produto) ? true : undefined;
            return { ...linha, produto, feeBaseInternacional, feeBaseComServico, feeBaseComImpostoItem, feeBaseNfAccess: undefined, fatorMoedaOrcamento: undefined, baseFeeBrlExato: undefined,
                totalFechamento: calculado.completo ? calculado.principal_brl : numeroOuZero(campo('total')) * cambioLinha,
                valorUnitarioFechamento: calculado.completo ? calculado.principal_brl : numeroOuZero(campo('valorUnitario')) * cambioLinha,
                quantidadeFechamento: calculado.completo ? 1 : campo('quantidade'), diariaFechamento: calculado.completo ? 1 : campo('diaria'), servicoFechamento: calculado.completo ? 0 : campo('servico'),
                inserirCalculoTotalPlanilha: calculado.completo ? false : linha.inserirCalculoTotalPlanilha,
            };
        }) }));
        // D-0153: NF Access da linha sobre o total do shadow (R$); ledger concluído sai da base da NF.
        if (baseComNfAccess) copia.forEach((grupo) => grupo.subitem.forEach((linha) => {
            if (linha.feeBaseComServico) anotarNfAccessNaBaseFee(grupo, linha, fornecedorAccess, { pre_evento: 0, negociado: 0, fechamento: linha.totalFechamento });
        }));
        copia.forEach((grupo, g) => grupo.subitem.forEach((linha, l) => {
            if (!FeeIntelligence.isFeeCalculatorItem(linha.produto)) return;
            const meta = FeeIntelligence.calculateFeeRealMetadata(copia, linha, {
                feeRules: regras,
                tabelaVersao: orcamento.tabela_versao,
                faixasCliente: contexto.faixasVersao || [],
                // Pax = "Número de convidados esperados" do evento (01/10), não o quantidadeConvidados do RSVP.
                paxEvento: contexto.paxEvento ?? paxConvidadosEsperados(convidadosEsperadosDoEvento(orcamento.evento)),
                // Mini Meeting (28/09): regra do cliente já filtrada pelo tipo do evento.
                miniMeeting: contexto.miniMeeting ?? null,
            });
            if (!meta) return;
            if (!meta.completo) regrasCompletas = false;
            // A mesma regra determina o valor; ausência de taxa não é inferida do FEE antigo.
            const total = meta.completo ? arredondarBrl(meta.total_brl) : null;
            if (total !== null) linha.totalFechamento = total;
            const chave = `${g}:${l}`;
            metadataFees.set(chave, { ...metadataFees.get(chave), ...(atual ? { base_atual_brl: arredondarBrl(meta.base_brl), percentual_atual: meta.percentual, total_atual_brl: total, completo_atual: meta.completo } : { base_brl: arredondarBrl(meta.base_brl), percentual: meta.percentual, total_brl: total,
                    contribuicao_brl: meta.completo ? arredondarBrl(meta.contribuicao_brl) : null, imposto_nf_percentual: meta.imposto_nf_percentual, completo: meta.completo, regra: meta.regra ?? null }) });
        }));
        if (chiesi) {
            copia.forEach((grupo) => {
                if (!AgenciaOrcamentoPolicy.isGrupoServicosAgencia(grupo)) return;
                const linhaNf = grupo.subitem.find((linha) => String(linha.produto?.codigo) === '1945');
                // D-0112: a 1945 marcada na conta direta cobra base × 19,05% (base equivalente).
                if (linhaNf) linhaNf.totalFechamento = impostoNaMoedaDoOrcamento(grupo.subitem.filter((linha) => linha !== linhaNf).reduce((soma, linha) => soma + numeroOuZero(linha.totalFechamento), 0) * fatorBaseNf(linhaNf), cambio);
            });
            ChiesiAgenciaImpostoPolicy.sincronizar(copia, true, fornecedorAccess);
        }
        let total = copia.reduce((soma, grupo) => soma + grupo.subitem.reduce((s, linha) => (ehSomenteVisualizacao(linha) ? s : s + numeroOuZero(linha.totalFechamento)), 0), 0);
        let nfProprio = 0;
        const nfGrupos = copia.map(() => null);
        copia.forEach((grupo, g) => {
            const manual = ChiesiAgenciaImpostoPolicy.resolver(grupo, chiesi);
            if (manual && !manual.erro) nfProprio += numeroOuZero(manual.linha.totalFechamento);
            else if (chiesi && AgenciaOrcamentoPolicy.isGrupoServicosAgencia(grupo)) nfProprio += grupo.subitem.filter((linha) => String(linha.produto?.codigo) === '1945').reduce((soma, linha) => soma + numeroOuZero(linha.totalFechamento), 0);
            else if (aplicaNf) {
                const base = grupo.subitem.filter((linha) => compoeBaseNfProprio(linha, fornecedorAccess)).reduce((soma, linha) => soma + numeroOuZero(linha.totalFechamento) * fatorBaseNf(linha), 0);
                const nf = imposto(base);
                nfGrupos[g] = nf;
                total += nf;
                nfProprio += nf;
            }
        });
        total += reais.filter((calculado, i) => !opcao[i] && calculado.completo).reduce((soma, calculado) => soma + calculado.adicional_brl, 0);
        if (atual) total += adicionaisEstimadosAtual;
        return { total: arredondarBrl(total), nfProprio: arredondarBrl(nfProprio), regrasCompletas, copia, nfGrupos };
    };
    const fechado = ativoAtual ? shadow(false) : null;
    const atual = ativoAtual ? shadow(true) : null;
    const regrasFechamentoCompletas = fechado?.regrasCompletas ?? false;
    const regrasAtualCompletas = atual?.regrasCompletas ?? false;
    // O status do fechamento realizado depende da sua própria faixa de FEE.
    // A faixa do shadow Atual continua independente e só controla o total Atual.
    const regrasCompletas = regrasFechamentoCompletas;
    // Exibição só existe com ao menos uma linha concluída; reabrir o ledger volta ao valor gravado.
    const cambioExibicao = cambio > 0 ? cambio : 1;
    const exibicaoLinhas = grupos.map((grupo, g) => (grupo.subitem || []).map((linha, l) => (fechado
        ? exibicaoLinhaFechamento(linha, calcularFechamentoExterior(linha, fornecedorAccess), fechado.copia[g]?.subitem[l], cambioExibicao) : null)));
    let totalExibicao = 0;
    const exibicaoGrupos = grupos.map((grupo, g) => {
        if (!fechado) return null;
        const gravadoNf = numeroOuZero(grupo.impostoPlanilhaFechamento);
        const nf = fechado.nfGrupos[g] !== null ? fechado.nfGrupos[g] / cambioExibicao : gravadoNf;
        let valor = nf;
        let mudou = Math.abs(nf - gravadoNf) * cambioExibicao > 0.005;
        // Subtotal na moeda do ORÇAMENTO (ISAD 21/10): a célula da linha com moeda própria fica na moeda
        // dela (€ 800), então o ledger entra pelo R$ pago e as demais linhas pelo câmbio da linha.
        (grupo.subitem || []).forEach((linha, l) => {
            const exibicao = exibicaoLinhas[g][l];
            if (exibicao) mudou = true;
            if (exibicao?.origem === 'ledger') valor += exibicao.principal_brl / cambioExibicao + (exibicao.encargos_total_moeda_orcamento || 0);
            else if (exibicao) valor += exibicao.totalFechamento;
            else valor += numeroOuZero(linha.totalFechamento) * cambioBrlLinha(linha, 'Fechamento', cambioExibicao) / cambioExibicao;
        });
        totalExibicao += valor;
        return mudou ? { valorTotalFechamento: valor, impostoPlanilhaFechamento: nf } : null;
    });
    const semExibicao = (objeto) => { const { fechamento_exibicao: descartado, ...resto } = objeto; return resto; };
    const comExibicao = (objeto, exibicao) => (exibicao ? { ...semExibicao(objeto), fechamento_exibicao: exibicao } : semExibicao(objeto));
    return { ...comExibicao(orcamento, fechado && exibicaoGrupos.some(Boolean) ? { valorTotalFechamento: totalExibicao } : null),
        fechamento_real_ativo: ativo, fechamento_real_ativo_atual: ativoAtual,
        fechamento_real_completo: ledgerCompleto && regrasCompletas,
        totalRealFechamentoBRL: regrasFechamentoCompletas ? fechado?.total ?? null : null, totalRealAtualBRL: regrasAtualCompletas ? atual?.total ?? null : null,
        fechamento_real_nf_proprio_brl: regrasFechamentoCompletas ? fechado?.nfProprio ?? null : null,
        fechamento_real_nf_proprio_atual_brl: regrasAtualCompletas ? atual?.nfProprio ?? null : null,
        fechamento_real_status: !ativo ? 'inativo' : ledgerCompleto && regrasCompletas ? 'concluido' : ativoAtual ? 'misto_parcial' : 'parcial',
        orcamentoitem: grupos.map((grupo, g) => ({ ...comExibicao(grupo, exibicaoGrupos[g]), subitem: (grupo.subitem || []).map((linha, l) => ({ ...comExibicao(linha, exibicaoLinhas[g][l]),
            fechamento_exterior_calculado: calcularFechamentoExterior(linha, fornecedorAccess),
            fee_fechamento_real_calculado: metadataFees.get(`${g}:${l}`) || null,
        })) })),
    };
}
